import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { format } from 'date-fns';
import { SystemFeedback } from 'cat-ecommerce-alloy';
import ExceptionFeedbackBySelector from '@app/components/common/Exception/ExceptionFeedbackBySelector';
import ExceptionFeedback from '@app/components/common/Exception/ExceptionFeedback';
import SummaryAndPaymentHeaderActions from '../SummaryAndPaymentHeaderActions';
import StopEditingQuote from '../StopEditingQuote';
import { CSR_TEXTS } from '@app/components/pages/csr-landing/texts';
import { addressProps, billingMethodProps } from '../declarations.propTypes';
import { replaceTokensInString } from '@app/utils';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  FEEDBACK_TYPES
} from '@app/constants/errorConstants';
import {
  ORDER_STATUS,
  STATUS,
  USER_ROLES
} from '@app/constants/commonConstants';
import { ORDER_TYPE_QOT } from '../../account/common/OrdersToApproveTable/constants';
import { getPaymentSummaryHeading } from '../utils';
import { formatDateAndTime } from '../../checkout/utils';
import {
  PCC_PS_TITLE_UPDATES,
  PCC_REFRESH_PARTS_AVAILABILITY_ON_QUOTE
} from '@app/constants/featureFlags';
import SummaryAndPaymentCriticalErrorMessage from '../SummaryAndPaymentCriticalErrorMessage/SummaryAndPaymentCriticalErrorMessage';
import styles from './SummaryAndPaymentPageHeader.module.scss';

const SummaryAndPaymentPageHeader = ({
  isPendingQuote,
  isSAndPLoadSuccessful,
  orderId,
  orderHeader,
  selectedBillingAddress = {},
  selectedBillingMethod = {},
  quoteAvailable,
  originalQuoteId,
  originalQuoteNumber,
  hideQuoteDetails,
  isWarningDisplay,
  successDisplay
}) => {
  const [t] = useTranslation();

  const checkoutErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.CHECKOUT]?.[ERROR_PATH.MAIN];

  const pendingQuoteErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.PENDING_QUOTE_DETAILS]?.[ERROR_PATH.MAIN];

  const timeoutErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.CHECKOUT]?.[ERROR_PATH.MAIN_TIMEOUT_ERROR];

  const networkErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.CHECKOUT]?.[ERROR_PATH.MAIN_NETWORK_ERROR];
  const pendingQuotesStatus = useSelector(
    s => s.orders.byId[orderId]?.dealerDocumentStatus
  );

  const orderCallStatus = useSelector(
    s => s?.orders?.byId[orderId]?.orderCallStatus
  );
  const formattedSubmitTime = useSelector(
    s => s.orders?.byId[orderId]?.formattedSubmitTime
  );
  const pendingApprovalQuotesStatus = useSelector(
    s => s.orders?.byId[orderId]?.orderStatus
  );
  const isQuoteExpired = useSelector(
    s => s?.orders?.byId[orderId]?.isQuoteExpired
  );

  const refreshPartsAvailabilityOnQuote = useSelector(
    s => s.featureFlag?.[PCC_REFRESH_PARTS_AVAILABILITY_ON_QUOTE]
  );
  const quoteType = useSelector(s => s.orders?.byId[orderId]?.orderType);
  const isCalUserWithAuhority = useSelector(
    s => s.orders?.byId[orderId]?.isCalUserWithAuhority
  );
  const isPendingApproval =
    pendingApprovalQuotesStatus === ORDER_STATUS.PENDING_APPROVAL;
  const isOrderTypeQot = quoteType === ORDER_TYPE_QOT;
  const isShowQuoteStatus = isPendingApproval && isOrderTypeQot;
  const isApproverQuoteStatus = isShowQuoteStatus && isCalUserWithAuhority;
  const userRoles = useSelector(s => s.common.userRoles);
  const orderStatus = useSelector(s => s.orders?.byId[orderId]?.orderStatus);
  const isRejected = orderStatus === ORDER_STATUS.REJECTED;
  const isPendingApprovalQuote = isPendingApproval && isOrderTypeQot;
  const isRejectedQuote = isRejected && isOrderTypeQot;
  const isSubmitter = userRoles?.includes(USER_ROLES.SUBMITTER);
  const isAdmin = userRoles?.includes(USER_ROLES.ADMIN);
  const skipRefreshPartsAvailibility =
    isQuoteExpired ||
    isRejectedQuote ||
    (isPendingApprovalQuote && isSubmitter && !isAdmin);

  const pendingPageResolved = orderCallStatus === STATUS.RESOLVED;
  const dealerName = useSelector(s => s.dealer?.dealerName);
  const dealerImg = useSelector(s => s.dealer?.imageURL);
  const isBuyOnBehalf = useSelector(s => s.common?.isBuyOnBehalf) || false;
  const checkoutError = useSelector(checkoutErrorSelector);
  const isPCCPaymentAndSummaryTitleUpdates = useSelector(
    state => state?.featureFlag?.[PCC_PS_TITLE_UPDATES]
  );
  const PCC_CustomerSupportErrorEventLoggingSummaryAndPayment = useSelector(
    state =>
      state?.featureFlag?.PCC_CustomerSupportErrorEventLoggingSummaryAndPayment
  );
  const timeoutError = useSelector(timeoutErrorSelector);
  const networkError = useSelector(networkErrorSelector);
  const dealerDateFormat = useSelector(s => s.dealer?.dealerDateFormat);
  const dateAndTime = formatDateAndTime(dealerDateFormat);
  const { formattedDate, formattedTime } = dateAndTime;

  const isPendingQuotesOnHold = pendingQuotesStatus === ORDER_STATUS.HOLD;
  const numberOrder = isPendingQuote && orderHeader?.quoteDocumentNumber;
  const errorSelector = isPendingQuote
    ? pendingQuoteErrorSelector
    : checkoutErrorSelector;

  const paymentSummaryHeading = getPaymentSummaryHeading(
    t,
    isPCCPaymentAndSummaryTitleUpdates
  );

  const getPageHeaderTitle = () => {
    if (isPendingQuote && pendingPageResolved) {
      return t('MO_QUOTEDETAILS');
    } else if (isSAndPLoadSuccessful) {
      return paymentSummaryHeading;
    }
  };

  const getExceptionBlock = () => {
    if (isBuyOnBehalf && typeof checkoutError !== 'undefined') {
      return (
        <SystemFeedback
          type={'error'}
          className={styles.summaryAndPaymentHeader}
          title={t('UNKNOWN_ERROR_TITLE')}
          message={CSR_TEXTS.CSR_ORDER_PLACEMENT_UNAVAILABLE}
        />
      );
    }

    if (timeoutError) {
      return (
        <ExceptionFeedbackBySelector
          selector={timeoutErrorSelector}
          showContactUs
          showDate
        />
      );
    }

    if (networkError) {
      return PCC_CustomerSupportErrorEventLoggingSummaryAndPayment ? (
        <SummaryAndPaymentCriticalErrorMessage
          selector={networkErrorSelector}
        />
      ) : (
        <ExceptionFeedbackBySelector selector={networkErrorSelector} />
      );
    }

    return (
      <ExceptionFeedbackBySelector
        selector={errorSelector}
        className={styles.summaryAndPaymentHeader}
        showReferenceId
      />
    );
  };

  return (
    <div className="row">
      <div className="d-none col d-print-block">
        <img
          src={`${dealerImg}?fmt=png-alpha&wid=124&hei=30`}
          alt={`${dealerName}-img`}
        />
        <span
          className={cx(
            'd-print-block col text-uppercase mt-4 color-extra-dark-gray text-xxl'
          )}
        >
          <b>
            {dealerName} {numberOrder}
          </b>
        </span>
      </div>
      <div className="col d-print-none">
        {!isPendingQuote && originalQuoteNumber && (
          <StopEditingQuote
            originalQuoteNumber={originalQuoteNumber}
            originalQuoteId={originalQuoteId}
            orderId={orderId}
          />
        )}

        <div
          className={cx('d-flex justify-content-between align-items-end', {
            'mb-2': !isPCCPaymentAndSummaryTitleUpdates,
            'mb-0 mb-md-2': isPCCPaymentAndSummaryTitleUpdates,
            [styles['title__border']]: !isPCCPaymentAndSummaryTitleUpdates
          })}
        >
          <h1
            className={cx('font-weight-normal m-0', {
              'text-capitalize': isPCCPaymentAndSummaryTitleUpdates,
              'text-uppercase': !isPCCPaymentAndSummaryTitleUpdates,
              [styles['title']]: isPCCPaymentAndSummaryTitleUpdates
            })}
          >
            {getPageHeaderTitle()}
          </h1>

          {isSAndPLoadSuccessful && (
            <div className="float-end mt-lg-n1 mt-md-n2 m-0">
              <SummaryAndPaymentHeaderActions
                isPendingQuote={isPendingQuote}
                orderId={orderId}
                selectedBillingMethod={selectedBillingMethod}
                selectedBillingAddress={selectedBillingAddress}
                hideQuoteDetails={hideQuoteDetails}
              />
            </div>
          )}
        </div>

        {getExceptionBlock()}
        {isApproverQuoteStatus && !isQuoteExpired && (
          <div className="mt-4">
            <SystemFeedback
              type="warning"
              title={replaceTokensInString(
                t('QUOTE_EXCEEDED_LIMIT_TITLE_MSG'),
                numberOrder
              )}
            >
              <span>
                <strong>{t('SUBMISSION_DATE')}</strong> {formattedSubmitTime}
              </span>
            </SystemFeedback>
          </div>
        )}
        {isShowQuoteStatus && !isApproverQuoteStatus && !isQuoteExpired && (
          <div className="mt-4">
            <SystemFeedback
              type="warning"
              title={replaceTokensInString(
                t('QUOTE_EXCEEDED_LIMIT_TITLE_MSG'),
                numberOrder
              )}
              message={t('QUOTE_EXCEEDED_LIMIT_BODY_MSG')}
            />
          </div>
        )}

        {isPendingQuotesOnHold && quoteAvailable && (
          <ExceptionFeedback
            className="mb-3"
            preventScroll
            type={FEEDBACK_TYPES.ERROR}
          >
            <p>
              {replaceTokensInString(
                t('QUOTE_ONHOLD_MSG'),
                numberOrder,
                dealerName
              )}
            </p>
          </ExceptionFeedback>
        )}
        {successDisplay &&
          refreshPartsAvailabilityOnQuote &&
          isPendingQuote &&
          !skipRefreshPartsAvailibility && (
            <div className="mt-4">
              <SystemFeedback
                type="success"
                title={t('HAVE_REFRESHED_AVAIL')}
                message={replaceTokensInString(
                  t('REFRESHED_TIME_DATE'),
                  formattedDate,
                  formattedTime
                )}
              />
            </div>
          )}
        {refreshPartsAvailabilityOnQuote &&
          isWarningDisplay &&
          isPendingQuote &&
          !skipRefreshPartsAvailibility && (
            <div className="mt-4">
              <SystemFeedback
                type="warning"
                title={`${t('QUOTE_RETRIVE_AVAIL_ERROR')} ${t('PLEASE_CONTACT_DLR_FURTHER_ASSITANCE')}`}
              />
            </div>
          )}
      </div>
    </div>
  );
};

SummaryAndPaymentPageHeader.propTypes = {
  isPendingQuote: PropTypes.bool,
  isSAndPLoadSuccessful: PropTypes.bool,
  orderId: PropTypes.string,
  orderHeader: PropTypes.shape({
    orderByPhone: PropTypes.string,
    quoteDocumentNumber: PropTypes.string
  }),
  selectedBillingAddress: addressProps,
  selectedBillingMethod: billingMethodProps,
  originalQuoteId: PropTypes.string,
  originalQuoteNumber: PropTypes.string,
  quoteAvailable: PropTypes.bool,
  hideQuoteDetails: PropTypes.bool,
  isWarningDisplay: PropTypes.bool,
  successDisplay: PropTypes.bool
};

export default SummaryAndPaymentPageHeader;
